// objectsDatabase.js - Objects operations via Cloud Function
// Uses Firebase Cloud Function to write to ai-tourguide-1dc92 with admin permissions

import { getAuth } from 'firebase/auth';

const CLOUD_FUNCTION_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/museumObjectsHandler';

// Operations that require authentication
const WRITE_OPERATIONS = [
  'createMuseum', 'updateMuseum', 'deleteMuseum',
  'createObject', 'updateObject', 'deleteObject'
];

/**
 * Helper function to call the Cloud Function
 */
async function callCloudFunction(operation, data = {}) {
  try {
    const headers = {
      'Content-Type': 'application/json'
    };

    // Add auth token for write operations
    if (WRITE_OPERATIONS.includes(operation)) {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        throw new Error('Authentication required. Please sign in.');
      }
    }

    const response = await fetch(CLOUD_FUNCTION_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        operation,
        ...data
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Cloud Function error (${operation}):`, error);
    throw error;
  }
}

// Create a new object
export const createObject = async (museumId, objectData) => {
  console.log('DEBUG: Creating object for museum:', museumId, objectData);

  const data = {
    objectNumber: objectData.objectNumber,
    name: objectData.name,
    description: objectData.description || '',
    photoUrl: objectData.photoUrl || '',
    room: objectData.room || '',
    floor: objectData.floor || 1,
    category: objectData.category || 'other',
    artist: objectData.artist || '',
    period: objectData.period || '',
    materials: objectData.materials || [],
    dimensions: objectData.dimensions || '',
    inventoryNumber: objectData.inventoryNumber || '',
    featured: objectData.featured || false,
    visible: objectData.visible !== false,
    createdBy: objectData.createdBy || 'anonymous'
  };

  const result = await callCloudFunction('createObject', { museumId, data });
  console.log('Object created with ID:', result.id);
  return result;
};

// Update object record
export const updateObject = async (museumId, objectId, updates) => {
  return await callCloudFunction('updateObject', {
    museumId,
    objectId,
    data: updates
  });
};

// Delete object record
export const deleteObject = async (museumId, objectId) => {
  return await callCloudFunction('deleteObject', { museumId, objectId });
};

// Get single object by ID
export const getObject = async (museumId, objectId) => {
  return await callCloudFunction('getObject', { museumId, objectId });
};

// Get object by visitor-facing number (tag number)
export const getObjectByNumber = async (museumId, objectNumber) => {
  return await callCloudFunction('getObjectByNumber', {
    museumId,
    data: { objectNumber: parseInt(objectNumber) }
  });
};

// Get all objects with optional filters
export const getAllObjects = async (museumId, filters = {}) => {
  console.log('DEBUG: Fetching objects for museum:', museumId, 'with filters:', filters);
  return await callCloudFunction('getAllObjects', { museumId, filters });
};

// Get objects by room
export const getObjectsByRoom = async (museumId, roomId) => {
  return getAllObjects(museumId, { room: roomId });
};

// Get objects by category
export const getObjectsByCategory = async (museumId, category) => {
  return getAllObjects(museumId, { category });
};

// Get featured objects
export const getFeaturedObjects = async (museumId) => {
  return getAllObjects(museumId, { featured: true, visible: true });
};

// Get the next available object number
export const getNextObjectNumber = async (museumId) => {
  try {
    const objects = await getAllObjects(museumId, {});

    if (objects.length === 0) {
      return 1;
    }

    const maxNumber = Math.max(...objects.map(obj => obj.objectNumber || 0));
    return maxNumber + 1;
  } catch (error) {
    console.error('Error getting next object number:', error);
    return 1;
  }
};

// Check if object number is already in use
export const isObjectNumberAvailable = async (museumId, objectNumber, excludeObjectId = null) => {
  try {
    const objects = await getAllObjects(museumId, {});

    const found = objects.find(obj =>
      obj.objectNumber === parseInt(objectNumber) && obj.id !== excludeObjectId
    );

    return !found;
  } catch (error) {
    console.error('Error checking object number availability:', error);
    return false;
  }
};

// Search objects by name
export const searchObjects = async (museumId, searchTerm) => {
  try {
    const objects = await getAllObjects(museumId, { visible: true });

    const term = searchTerm.toLowerCase();
    return objects.filter(obj =>
      obj.name?.toLowerCase().includes(term) ||
      obj.description?.toLowerCase().includes(term) ||
      obj.artist?.toLowerCase().includes(term) ||
      obj.inventoryNumber?.toLowerCase().includes(term)
    );
  } catch (error) {
    console.error('Error searching objects:', error);
    throw new Error(`Failed to search objects: ${error.message}`);
  }
};

// Get object counts by room
export const getObjectCountsByRoom = async (museumId) => {
  try {
    const objects = await getAllObjects(museumId, {});

    const counts = {};
    objects.forEach(obj => {
      const room = obj.room || 'unassigned';
      counts[room] = (counts[room] || 0) + 1;
    });

    return counts;
  } catch (error) {
    console.error('Error getting object counts:', error);
    throw new Error(`Failed to get object counts: ${error.message}`);
  }
};

// Bulk update objects (e.g., reassign room)
export const bulkUpdateObjects = async (museumId, objectIds, updates) => {
  try {
    const results = [];

    for (const objectId of objectIds) {
      try {
        await updateObject(museumId, objectId, updates);
        results.push({ id: objectId, success: true });
      } catch (err) {
        results.push({ id: objectId, success: false, error: err.message });
      }
    }

    const successful = results.filter(r => r.success).length;
    console.log(`Bulk update: ${successful}/${objectIds.length} objects updated`);

    return {
      success: successful === objectIds.length,
      results,
      successCount: successful,
      failCount: objectIds.length - successful
    };
  } catch (error) {
    console.error('Error in bulk update:', error);
    throw new Error(`Failed to bulk update objects: ${error.message}`);
  }
};

const objectsDatabase = {
  createObject,
  updateObject,
  deleteObject,
  getObject,
  getObjectByNumber,
  getAllObjects,
  getObjectsByRoom,
  getObjectsByCategory,
  getFeaturedObjects,
  getNextObjectNumber,
  isObjectNumberAvailable,
  searchObjects,
  getObjectCountsByRoom,
  bulkUpdateObjects
};

export default objectsDatabase;
